// NeuesOfficeDokumentDialog.tsx — „Neues Dokument" am Vorgang: Word oder
// Excel, Name, anlegen, im Editor öffnen (User 28.09.2026: „bei Vorgängen
// neben Dokument hochladen auch neues Dokument und dann fragen Excel oder Word
// und Namen eingeben").
//
// Dieselbe Schale wie die Vorfrage-Dialoge des „+“-Menüs
// (`.kvp-rd.kvp-rd-scrim > .dm`), nur schmaler. Die Fragen selbst kommen aus
// OfficeArtUndName — derselbe Baustein wie dort.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Dismiss20Regular, DocumentAdd20Regular } from "@fluentui/react-icons";
import { OfficeOpenDialog } from '../documents/OfficeOpenDialog';
import { OfficeArtUndName } from "./OfficeArtUndName";
import { nameGueltig, type OfficeArt } from "./officeDokument";
import "../redesign/contactRedesign.css";
import "./neuAnlage.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export type OfficeDokumentAnlegen = (p: { art: OfficeArt; name: string }) => Promise<number>;

export function NeuesOfficeDokumentDialog({
  zielName,
  anlegen,
  tabVorbereiten,
  oeffnen,
  tabVerwerfen,
  onAngelegt,
  onClose,
}: {
  /** „am Vorgang 4711-2" o. Ä. — steht klein unter dem Titel. */
  zielName?: string | null;
  anlegen: OfficeDokumentAnlegen;
  tabVorbereiten: () => Window | null;
  oeffnen: (dokumentId: number, tab: Window | null) => void;
  tabVerwerfen: (tab: Window | null) => void;
  /** Nach dem Anlegen — z. B. die Dokumentliste neu laden. */
  onAngelegt?: (dokumentId: number) => void | Promise<void>;
  onClose: () => void;
}) {
  const [art, setArt] = React.useState<OfficeArt | null>(null);
  const [name, setName] = React.useState("");
  const [legtAn, setLegtAn] = React.useState(false);
  const [auswahl, setAuswahl] = React.useState(false);
  const [fehler, setFehler] = React.useState("");

  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !legtAn && !auswahl) {
        // Der Vorgang hat eigene Esc-Wege — die sollen nicht mitschließen.
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    alleFenster.addEventListener("keydown", esc);
    return () => alleFenster.removeEventListener("keydown", esc);
  }, [legtAn,auswahl]);

  const bereit = !!art && nameGueltig(name, art) && !legtAn;

  const los = () => { if (bereit) setAuswahl(true); };
  if (auswahl && art) return <OfficeOpenDialog
    createDocument={async()=>{const id=await anlegen({art,name:name.trim()});void Promise.resolve().then(()=>onAngelegt?.(id)).catch(()=>setFehler('Die Dokumentliste konnte nicht aktualisiert werden.'));return id;}}
    prepareTab={tabVorbereiten}
    discardTab={tabVerwerfen}
    openOnlyOffice={oeffnen}
    onOpened={()=>{}}
    onDismiss={()=>{setAuswahl(false);onClose();}}
  />;

  return createPortal(
    <div
      className="kvp-rd kvp-rd-scrim na-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !legtAn) onClose();
      }}
    >
      <div className="dm na-dm na-dm-klein" role="dialog" aria-modal="true" aria-label="Neues Dokument">
        <div className="dm-head">
          <div className="dm-head-top">
            <span className="dm-ico" aria-hidden>
              <DocumentAdd20Regular />
            </span>
            <div className="dm-titles">
              <div className="dm-kicker">{zielName || "Neu anlegen"}</div>
              <h2>Neues Dokument</h2>
            </div>
            <button type="button" className="dm-x" aria-label="Schließen" onClick={onClose} disabled={legtAn}>
              <Dismiss20Regular />
            </button>
          </div>
        </div>
        <div className="dm-body">
          <OfficeArtUndName art={art} onArt={setArt} name={name} onName={setName} onEnter={() => void los()} />
          {fehler && (
            <div className="na-fehler" role="alert">
              {fehler}
            </div>
          )}
        </div>
        <div className="dm-foot na-foot">
          <button type="button" className="na-btn" onClick={onClose} disabled={legtAn}>
            Abbrechen
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="na-btn primaer" disabled={!bereit} onClick={() => void los()}>
            {legtAn ? "Wird angelegt …" : "Anlegen und öffnen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default NeuesOfficeDokumentDialog;
